/**
 * Les sons que plusieurs écrans de l'élève partagent, et la façon de les jouer.
 *
 * `SAM_SOUNDS`, ceux de Sam dans l'ancien `SamConversationBase` et son `PetalRewardBadge` : son arrivée,
 * une réponse touchée, le pétale gagné. Le starting block, un checkpoint du live et le quiz de prérequis
 * les jouent, aux moments que leur conversation dit (`onSound`) ; le mode autonome reprend l'arrivée.
 *
 * `playStudentSound` prend la branche de l'ancien `PlaySound` qui convient : en live, celle du live (la
 * session audio est à l'appel : Android se tait, ailleurs le lecteur ne la rend pas) ; hors live, celle
 * d'avant l'appel (lecture en mode silencieux, les autres applis baissent le son). Un refus attendu (la
 * lecture automatique bloquée par le navigateur) ne dit rien ; le reste passe par la télémétrie.
 */
import { type AudioSource } from "expo-audio"

import { type CheckpointSound } from "../../../../../engine/precall/checkpoint/useCheckpointConversation"
import { playLiveSound } from "../../../../../engine/live/student/playLiveSound"
import { isExpectedPlaybackError, playSoundOnce } from "../../../../../engine/precall/playSound"
import { createTelemetry } from "../../../../../engine/shared/telemetry"

/* eslint-disable @typescript-eslint/no-require-imports */
export const SAM_SOUNDS: Record<CheckpointSound, number> = {
    open: require("../../../../../../assets/sounds/jingles_PIZZI14.mp3") as number,
    select: require("../../../../../../assets/sounds/select_002.mp3") as number,
    earned: require("../../../../../../assets/sounds/petals-gained-2.mp3") as number,
}
/* eslint-enable @typescript-eslint/no-require-imports */

const telemetry = createTelemetry("student.sounds", { role: "student" })

/**
 * Rend de quoi l'arrêter (« Passer l'animation » coupe le son du décompte, comme l'ancien
 * `PlaySoundM.stop()`). En live, le lecteur du live n'en rend pas : le son va au bout.
 */
export function playStudentSound(source: AudioSource, inLive: boolean, name: string): () => void {
    if (inLive) {
        playLiveSound(source, telemetry)
        return () => undefined
    }
    let stopped = false
    let player: { pause: () => void } | null = null
    playSoundOnce(source)
        .then((started) => {
            player = started
            if (stopped) started.pause()
        })
        .catch((error: unknown) => {
            if (!isExpectedPlaybackError(error)) telemetry.error("play_sound", error, { sound: name })
        })
    return () => {
        stopped = true
        player?.pause()
    }
}
